import React from 'react'
//import { makeStyles } from 'tss-react/mui'
import { classToFComponent, FCClassContext, Field, FieldHelp, FieldLabel, inputSx } from 'src/utils/views'
import { Logger } from 'wdc-cube'

import DeleteIcon from '@mui/icons-material/Delete'

import ClickAwayListener from '@mui/material/ClickAwayListener'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import MenuItem from '@mui/material/MenuItem'
import Popper from '@mui/material/Popper'
import TextField from '@mui/material/TextField'

import { Divider } from '@mui/material'
import {
    FilterAccordion,
    FilterAccordionDetails,
    FilterAccordionSummary
} from 'src/components/filteraccordion/filter_accordion'
import { lodash } from 'src/utils'
import { ViewSlot } from 'wdc-cube-react'
import { JunctionMode, StringFilterScope, StringOperator, StringParcelFilterScope } from '../../tcm_scopes'
import { DropdownBody, FieldIconButton, FilterPanel, criterionLabel } from 'src/utils/views/modules/the-consumer-market'

const LOG = Logger.get('StringFilterView')

//const useStyles = makeStyles()({})

type StringFilterViewProps = {
    className?: string
    scope: StringFilterScope
}

class StringFilterViewClass implements FCClassContext<StringFilterViewProps> {
    // :: Fields
    scope!: StringFilterScope

    onSyncState({ scope }: StringFilterViewProps) {
        this.scope = scope
    }

    render({ className }: StringFilterViewProps) {
        const { scope } = this

        return (
            <FilterAccordion className={className} expanded={scope.expanded} onChange={this.handleExpandedChange}>
                <FilterAccordionSummary
                    onClear={this.handleClearAllClicked}
                    onAdd={this.handlePlusClick}
                    caption={scope.caption}
                    captionBold={!!scope.conjunction.length || !!scope.disjunction.length}
                />
                <FilterAccordionDetails>
                    {!!scope.conjunction.length &&
                        scope.conjunction.map((parcelScope, idx, arr) => (
                            <StringParcelView
                                key={parcelScope.uid}
                                scope={parcelScope}
                                divider={idx < arr.length - 1 || !!scope.disjunction.length}
                            />
                        ))}

                    {!!scope.disjunction.length &&
                        scope.disjunction.map((parcelScope, idx, arr) => (
                            <StringParcelView
                                key={parcelScope.uid}
                                scope={parcelScope}
                                divider={idx < arr.length - 1}
                            />
                        ))}

                    {!scope.conjunction.length && !scope.disjunction.length && (
                        <FieldHelp>Nenhum critério adicionado. Use o + para incluir um.</FieldHelp>
                    )}
                </FilterAccordionDetails>
            </FilterAccordion>
        )
    } //

    readonly handleExpandedChange = (_event: React.SyntheticEvent, expanded: boolean) => {
        this.scope.onExpandedChange(expanded).catch(LOG.caught)
    }

    readonly handlePlusClick = () => {
        this.scope.onAddFilter(JunctionMode.OR).catch(LOG.caught)
    }

    readonly handleClearAllClicked = () => {
        this.scope.onClearAllFilters().catch(LOG.caught)
    }
}

export const StringFilterView = classToFComponent(StringFilterViewClass, React)
export default StringFilterView

// :: StringParcelViewClass

type StringParcelViewProps = {
    className?: string
    scope: StringParcelFilterScope
    divider?: boolean
}

class StringParcelViewClass implements FCClassContext<StringParcelViewProps> {
    scope!: StringParcelFilterScope

    valueField = new (class {
        scope!: StringParcelFilterScope
        elm?: HTMLDivElement | null
        focused = false

        readonly setRef = (el: HTMLDivElement) => {
            this.elm = el
        }

        readonly handleFocus = () => {
            this.focused = true
            this.scope.update()
        }

        readonly handleBlur = () => {
            this.focused = false
            this.scope.update()
        }

        readonly handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            this.scope.onValueChange(event.target.value).catch(LOG.caught)
        }

        readonly handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
            if (e.key === 'Enter') {
                this.scope.onEnterKeyPressed().catch(LOG.caught)
            }
        }

        readonly handleClearClick = () => {
            this.scope.onClear().catch(LOG.caught)
        }
    })()

    popper = new (class {
        scope!: StringParcelFilterScope
        open = false
    })()

    render({ className, scope, divider }: StringParcelViewProps) {
        this.scope = scope
        this.valueField.scope = scope
        this.popper.scope = scope

        if (scope.valueVisible && this.valueField.focused) {
            const searchText = scope.value.trim()
            this.popper.open = scope.options.length > 0 && searchText.length > 0
        } else if (scope.options.length === 0) {
            this.popper.open = false
        }

        return (
            <>
                <FilterPanel className={className}>
                    {lodash.isNotNil(scope.extra) && <ViewSlot scope={scope.extra} />}
                    <Field>
                        <FieldLabel htmlFor={scope.uid}>
                            {criterionLabel(scope.junctionMode === JunctionMode.AND)}
                        </FieldLabel>
                        <TextField
                            id={scope.uid}
                            select
                            size="small"
                            defaultValue={`${StringOperator.contains}`}
                            value={`${scope.operator}`}
                            onChange={this.handleSelectChange}
                            sx={inputSx}
                            fullWidth
                        >
                            <MenuItem value={StringOperator.contains}>Contém</MenuItem>
                            <MenuItem value={StringOperator.does_not_contain}>Não contém</MenuItem>
                            <MenuItem value={StringOperator.starts_with}>Começa com</MenuItem>
                            <MenuItem value={StringOperator.does_not_start_with}>Não começa com</MenuItem>
                            <MenuItem value={StringOperator.is}>É igual</MenuItem>
                            <MenuItem value={StringOperator.is_not}>Não é igual</MenuItem>
                            {scope.acceptBlankValues && (
                                <MenuItem value={StringOperator.is_blank}>Está em branco</MenuItem>
                            )}
                            {scope.acceptBlankValues && (
                                <MenuItem value={StringOperator.is_not_blank}>Não está em branco</MenuItem>
                            )}
                            {scope.acceptBlankValues && <MenuItem value={StringOperator.is_empty}>Está vazio</MenuItem>}
                            {scope.acceptBlankValues && (
                                <MenuItem value={StringOperator.is_not_empty}>Não está vazio</MenuItem>
                            )}
                            {scope.hasInOption && <MenuItem value={StringOperator.is_in}>Está na lista</MenuItem>}
                            {scope.hasInOption && (
                                <MenuItem value={StringOperator.is_not_in}>Não está na lista</MenuItem>
                            )}
                        </TextField>
                    </Field>
                    {scope.valueVisible && (
                        <ClickAwayListener onClickAway={this.handleClickAway}>
                            <Field>
                                <FieldLabel htmlFor={`${scope.uid}-valor`}>Valor</FieldLabel>
                                <TextField
                                    id={`${scope.uid}-valor`}
                                    ref={this.valueField.setRef}
                                    size="small"
                                    autoComplete="off"
                                    value={scope.value}
                                    onChange={this.valueField.handleChange}
                                    onKeyDown={this.valueField.handleKeyDown}
                                    onFocus={this.valueField.handleFocus}
                                    onBlur={this.valueField.handleBlur}
                                    sx={inputSx}
                                    fullWidth
                                    slotProps={{
                                        input: {
                                            endAdornment: (
                                                <FieldIconButton
                                                    size="small"
                                                    aria-label="Limpar valor"
                                                    onClick={this.valueField.handleClearClick}
                                                >
                                                    <DeleteIcon />
                                                </FieldIconButton>
                                            )
                                        }
                                    }}
                                />

                                <Popper open={this.popper.open} anchorEl={this.valueField.elm} placement="bottom-start">
                                    <DropdownBody
                                        sx={{ width: this.valueField.elm ? this.valueField.elm.offsetWidth : 184 }}
                                    >
                                        <List dense={true}>
                                            {scope.options.map((label, i) => {
                                                return (
                                                    <ListItem key={i} disablePadding>
                                                        <ListItemButton onClick={() => this.handleListItemClick(label)}>
                                                            <ListItemText primary={label} />
                                                        </ListItemButton>
                                                    </ListItem>
                                                )
                                            })}
                                        </List>
                                    </DropdownBody>
                                </Popper>
                            </Field>
                        </ClickAwayListener>
                    )}
                </FilterPanel>
                {divider && <Divider sx={{ borderColor: 'var(--border)' }} />}
            </>
        )
    }

    readonly handleSelectChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        this.scope.onOperatorChange(+event.target.value)
    }

    readonly handleClickAway = () => {
        this.popper.open = false
        this.scope.update()
    }

    readonly handleListItemClick = (label: string): void => {
        this.scope.onOptionSelected(label).catch(LOG.caught)
    }
}

export const StringParcelView = classToFComponent(StringParcelViewClass, React)
